import { AlertCircle, CheckCircle2, Clock, Link as LinkIcon } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { USER_ROUTES } from "@/routes";

interface EventContributionsSectionProps {
  totalContributions: number;
  currency: string;
  plan: 'free' | 'standard' | 'premium';
  bankLinked: boolean;
  bankLinkStatus?: 'linked' | 'action_required' | 'pending' | 'not_linked';
  currentLocale: string;
  /** JUB-124: carries the event into the Pachete page so the purchase binds to it. */
  eventUuid?: string;
}

export function EventContributionsSection({
  totalContributions,
  currency,
  plan,
  bankLinked,
  bankLinkStatus,
  currentLocale,
  eventUuid,
}: EventContributionsSectionProps) {
  const { t } = useTranslation();
  const resolvedStatus = bankLinkStatus ?? (bankLinked ? 'linked' : 'not_linked');
  
  const formatCurrency = (value: number, currencyCode: string) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
        maximumFractionDigits: 0,
      }).format(value);
    } catch {
      return `${value.toLocaleString()} ${currencyCode}`;
    }
  };
  const statusLabels = {
    linked: t('events.stats.bankLinked'),
    action_required: t('events.stats.bankActionRequired'),
    pending: t('events.stats.bankPending'),
    not_linked: t('events.stats.bankNotLinked'),
  };
  const statusStyles = {
    linked: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50',
    action_required: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-900/50',
    pending: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/50',
    not_linked: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600',
  };
  const statusIcon = {
    linked: <CheckCircle2 className="h-3.5 w-3.5" />,
    action_required: <AlertCircle className="h-3.5 w-3.5" />,
    pending: <Clock className="h-3.5 w-3.5" />,
    not_linked: <LinkIcon className="h-3.5 w-3.5 opacity-50" />,
  };

  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 p-4 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <div className="p-1.5 rounded-full bg-pink-100 dark:bg-pink-900/40">
          <CheckCircle2 className="h-4 w-4 text-pink-600 dark:text-pink-400" />
        </div>
        <div>
          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(totalContributions, currency)}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            {t('events.stats.totalContributions')}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a
          href={eventUuid ? `/${currentLocale}/pachete?event=${eventUuid}` : `/${currentLocale}/pachete`}
          onClick={(e) => e.stopPropagation()}
          className="px-3 py-1 rounded-full bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-300 text-sm font-medium whitespace-nowrap hover:bg-pink-200 dark:hover:bg-pink-900/50 transition-colors"
        >
          {t(`landing.pricing.${plan}.name`) || t(`pricing.${plan}.name`) || (plan === 'free' ? 'Gratuit' : plan === 'standard' ? 'Standard' : 'Premium')}
        </a>
        {/* Only show bank status for standard/premium plans */}
        {(plan === 'standard' || plan === 'premium') && (
          <a
            href={USER_ROUTES.connectedAccounts(currentLocale)}
            onClick={(e) => e.stopPropagation()}
            className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap flex items-center gap-1 transition-colors ${
              statusStyles[resolvedStatus]
            }`}
            data-testid="event-card-bank-badge"
          >
            {statusIcon[resolvedStatus]}
            {statusLabels[resolvedStatus]}
          </a>
        )}
      </div>
    </div>
  );
}

